// App lock: a 4- or 6-digit PIN (plus optional fingerprint / Face ID) that must be entered when the app starts and after it has been
// in the background for the chosen time.
//
// What this is, honestly: a screen lock that keeps people who pick up your unlocked phone or browser out of the app. It is NOT
// encryption of the data on the device. Someone with deep access to the device's storage could still read it, so keep your
// phone's own lock screen on. The PIN is stored only as a salted PBKDF2 hash, wrong guesses are slowed down, and a forgotten
// PIN can only be reset by erasing this device's data (which a synced account, or a backup file, restores).

const KEY = "bb-applock-v1";
export const PIN_LENGTHS = [4, 6];
export const DEFAULT_PIN_LENGTH = 6;
const ITER = 150000;
const FREE_TRIES = 5;           // after this many wrong PINs in a row, each further wrong PIN waits longer
const MAX_WAIT_SEC = 900;

const enc = new TextEncoder();
const b64 = (buf) => { const b = new Uint8Array(buf); let s = ""; for (let i = 0; i < b.length; i++) s += String.fromCharCode(b[i]); return btoa(s); };
const unb64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch (e) { return null; } };
const write = (o) => { try { localStorage.setItem(KEY, JSON.stringify(o)); } catch (e) { /* ignore */ } };

export const lockEnabled = () => !!read()?.hash;
export const pinLength = () => read()?.len || DEFAULT_PIN_LENGTH;
export const lockSettings = () => { const c = read(); return c?.hash ? { autoLockSec: c.autoLockSec ?? 60, biometric: !!c.bioCred, pinLength: c.len || DEFAULT_PIN_LENGTH } : null; };

async function hashPin(pin, saltB64, iter) {
  const base = await crypto.subtle.importKey("raw", enc.encode(pin), "PBKDF2", false, ["deriveBits"]);
  return b64(await crypto.subtle.deriveBits({ name: "PBKDF2", salt: unb64(saltB64), iterations: iter, hash: "SHA-256" }, base, 256));
}
const sameString = (a, b) => { if (a.length !== b.length) return false; let d = 0; for (let i = 0; i < a.length; i++) d |= a.charCodeAt(i) ^ b.charCodeAt(i); return d === 0; };

// "" if the PIN is acceptable, otherwise a short reason. `len` is the length the person chose (4 or 6).
export function pinProblem(pin, len = String(pin || "").length) {
  const p = String(pin || "");
  if (!PIN_LENGTHS.includes(len)) return "Choose a 4 or 6 digit PIN.";
  if (!new RegExp(`^\\d{${len}}$`).test(p)) return `Use exactly ${len} digits.`;
  if (/^(\d)\1+$/.test(p)) return "Don't repeat one digit.";
  const asc = "01234567890", desc = "09876543210";
  if (asc.includes(p) || desc.includes(p)) return "Don't use a straight run like 1234.";
  const half = p.slice(0, len / 2);
  if (p === half + half) return "Don't repeat a short pattern.";
  return "";
}

export async function setPin(pin, { autoLockSec } = {}) {
  const problem = pinProblem(pin);
  if (problem) throw new Error(problem);
  const old = read() || {};
  const salt = b64(crypto.getRandomValues(new Uint8Array(16)));
  write({ v: 1, salt, iter: ITER, hash: await hashPin(pin, salt, ITER), len: String(pin).length, autoLockSec: autoLockSec ?? old.autoLockSec ?? 60, bioCred: old.bioCred || null, fails: 0, until: 0 });
}

export const lockoutRemaining = (now = Date.now()) => { const c = read(); return c?.until > now ? Math.ceil((c.until - now) / 1000) : 0; };

// -> { ok: true } | { ok: false, wait: seconds } | { ok: false, triesLeft: n }
export async function verifyPin(pin, now = Date.now()) {
  const c = read();
  if (!c?.hash) return { ok: true };
  const wait = lockoutRemaining(now);
  if (wait > 0) return { ok: false, wait };
  const good = sameString(await hashPin(String(pin), c.salt, c.iter), c.hash);
  if (good) { write({ ...c, fails: 0, until: 0 }); return { ok: true }; }
  const fails = (c.fails || 0) + 1;
  if (fails >= FREE_TRIES) {
    const sec = Math.min(MAX_WAIT_SEC, 30 * 2 ** (fails - FREE_TRIES));
    write({ ...c, fails, until: now + sec * 1000 });
    return { ok: false, wait: sec };
  }
  write({ ...c, fails });
  return { ok: false, triesLeft: FREE_TRIES - fails };
}

export function setAutoLock(sec) { const c = read(); if (c?.hash) write({ ...c, autoLockSec: sec }); }
export function removeLock() { try { localStorage.removeItem(KEY); } catch (e) { /* ignore */ } }
// "Forgot PIN": the only way back in is to wipe this device's data. A synced account or a backup file restores it.
export function resetLockAndDeviceData() { try { localStorage.removeItem("vellum-data"); localStorage.removeItem(KEY); } catch (e) { /* ignore */ } }

// ---- optional fingerprint / Face ID (WebAuthn platform authenticator). It only asks the device to verify the person;
// it is a convenience on top of the PIN, and the PIN always still works.
export async function biometricAvailable() {
  try { return !!(window.PublicKeyCredential && (await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable())); }
  catch (e) { return false; }
}
export async function enrollBiometric() {
  const c = read();
  if (!c?.hash) throw new Error("Set a PIN first.");
  const cred = await navigator.credentials.create({
    publicKey: {
      challenge: crypto.getRandomValues(new Uint8Array(32)),
      rp: { name: "Balance & Beyond" },
      user: { id: crypto.getRandomValues(new Uint8Array(16)), name: "app-lock", displayName: "App lock" },
      pubKeyCredParams: [{ type: "public-key", alg: -7 }, { type: "public-key", alg: -257 }],
      authenticatorSelection: { authenticatorAttachment: "platform", userVerification: "required", residentKey: "discouraged" },
      timeout: 60000, attestation: "none",
    },
  });
  write({ ...c, bioCred: b64(cred.rawId) });
}
export function disableBiometric() { const c = read(); if (c) write({ ...c, bioCred: null }); }
export async function biometricUnlock() {
  const c = read();
  if (!c?.bioCred) return false;
  try {
    await navigator.credentials.get({ publicKey: { challenge: crypto.getRandomValues(new Uint8Array(32)), allowCredentials: [{ type: "public-key", id: unb64(c.bioCred), transports: ["internal"] }], userVerification: "required", timeout: 60000 } });
    return true;
  } catch (e) { return false; }
}
